/* eslint-disable @typescript-eslint/no-unused-vars */
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { IoIosArrowBack } from 'react-icons/io';

import { useIsDesktop } from '../../hooks/useIsDesktop';
import Dropdown from '../Dropdown';
import { DefaultTabsProps, Tabs } from '../Tabs';
import { TabType } from '../Types';

import { ItemAssistant } from './ItemsAssistant';
import { ReferencesFieldAssistance } from './ReferencesFieldAssistance';

import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { MyPlayersScore, PartidoUltimaFecha } from '@/types/fieldAssistance';
import 'moment/dist/locale/es';
import { VariantFieldAssistance } from '@/types/team';
import { FADE_IN_OUT, MATCH_STATUS, cn } from '@/utils';

interface Props extends VariantFieldAssistance {
  matchs: PartidoUltimaFecha[];
}

export const ScoreLastMatchTable = ({ matchs, variant }: Props) => {
  const { tab, handleTabChanges } = useHandleChangesTabs();
  const [state, setState] = useState(0);
  const { isDesktop, isMobile } = useIsDesktop();

  // lleno tabla de partidos
  const printFixture = useMemo(() => {
    return matchs.map((match: PartidoUltimaFecha, index: number) => {
      const matchData = match.data;
      return (
        <div key={index}>
          <ItemAssistant.Fixture
            golsLocal={match.ptosEquipoLocal}
            golsVisitor={match.ptosEquipoVisitante}
            idEstado={match.idEstado}
            idLocal={matchData.idLocal}
            idVisitor={matchData.idVisitante}
            localClub={matchData.equipoLocal}
            variant={'SCORE_LAST_MATCH'}
            visitorClub={matchData.equipoVisitante}
            onClick={() => setState(index + 1)}
          />
        </div>
      );
    });
  }, [matchs, isDesktop]);

  // lleno tablas de jugadores
  const printMatch = useMemo(() => {
    const indexMatch = state === 0 ? 1 : state - 1;

    const matchSelected =
      tab === TabType.FirstTab
        ? matchs[indexMatch]?.puntajeJugadoresEquipoLocal
        : matchs[indexMatch]?.puntajeJugadoresEquipoVisitante;

    if (isMobile) {
      return matchSelected?.map((player: MyPlayersScore, index: number) => {
        return (
          <div key={index}>
            <ItemAssistant.Default2 player={player} />
          </div>
        );
      });
    } else {
      // render items desktop equipo local
      return matchSelected?.map((player: MyPlayersScore, index: number) => {
        return (
          <div key={index}>
            <ItemAssistant.Incidents
              player={player}
              variant="SCORE_LAST_MATCH"
            />
          </div>
        );
      });
    }
  }, [isMobile, matchs, state, tab]);

  // lleno combo con opciones de partidos
  const matchComboOptions = useMemo(() => {
    const options = matchs.map((match: PartidoUltimaFecha, index: number) => {
      const {
        data: matchData,
        idEstado,
        ptosEquipoLocal,
        ptosEquipoVisitante,
      } = match;

      const label =
        idEstado === MATCH_STATUS.SUSPENDIDO
          ? `${matchData.equipoLocal} vs. ${matchData.equipoVisitante}`
          : `${matchData.equipoLocal} ${ptosEquipoLocal} vs. ${matchData.equipoVisitante} ${ptosEquipoVisitante}`;

      const disabled = idEstado === MATCH_STATUS.SUSPENDIDO;

      return {
        label,
        value: index + 1,
        disabled,
      };
    });

    return [{ label: 'Elegí partido', value: 0 }, ...options];
  }, [matchs]);

  const tabs: DefaultTabsProps['tabs'] = useMemo(() => {
    return [
      { name: state > 0 ? matchs[state - 1].data.equipoLocal : '' },
      { name: state > 0 ? matchs[state - 1].data.equipoVisitante : '' },
    ];
  }, [matchs, state]);

  return (
    matchs.length > 0 && (
      <motion.div
        key={state}
        animate={'show'}
        className="relative"
        initial={'hide'}
        variants={FADE_IN_OUT}
      >
        {isMobile && (
          <div className="w-full lg:max-w-[400px] mt-4 lg:mt-0 px-4">
            <Dropdown
              options={matchComboOptions}
              selected={matchComboOptions[state]}
              onChange={(item) => {
                setState(item.value);
              }}
            />
          </div>
        )}
        {state > 0 && (
          <div className="px-4 mt-4 lg:mt-0 lg:absolute lg:-top-[72px] lg:left-[384px] lg:ml-3 w-full">
            <Tabs
              activeTab={tab}
              className="!h-[48px] mt-2 lg:mt-0"
              disabled={false}
              tabs={tabs}
              onClick={(tab: TabType) => handleTabChanges(tab)}
            />
          </div>
        )}
        <div
          className={cn('mt-4 lg:mt-6 lg:rounded-lg lg:overflow-hidden', {
            'overflow-auto h-[calc(100vh-170px)]': variant === 'EMBED',
          })}
        >
          {state === 0 ? (
            printFixture
          ) : (
            <>
              <div className="lg:pl-9 h-14 flex items-center lg:p-0 px-4 mt-4 lg:mt-0 lg:bg-white lg:justify-end lg:border-b-[1px] lg:border-b-gdt-gray-7">
                <p className="text-sm lg:text-xs text-gdt-gray-2 font-bold lg:hidden flex-1">
                  Lista de Jugadores
                </p>
                <div
                  className="text-sm lg:text-sm text-gdt-blue font-bold hidden lg:flex flex-1"
                  onClick={() => {
                    setState(0);
                    handleTabChanges(TabType.FirstTab);
                  }}
                >
                  <span className="flex items-center cursor-pointer">
                    <IoIosArrowBack className="m-auto" size={16} />
                    &nbsp;Volver
                  </span>
                </div>
                <div className="flex max-w-[100px] lg:max-w-[344px] w-full text-2xs lg:text-sm text-gdt-gray-4 font-semibold text-center">
                  <p className="flex-1 lg:max-w-[132px] text-center lg:text-center leading-tight">
                    {isMobile ? 'Ptos. Gran DT' : 'Puntos Gran DT'}
                  </p>
                  <p className="flex-1 lg:max-w-[132px] text-right leading-tight lg:text-center">
                    {isMobile ? 'Ptos. Clarín' : 'Puntos Clarín'}
                  </p>
                </div>
              </div>
              {<div className="bg-white">{printMatch}</div>}
              {isDesktop && <ReferencesFieldAssistance />}
            </>
          )}
        </div>
      </motion.div>
    )
  );
};
